/**
 * Connect tab — Interface section (task 380, Mockup M).
 *
 * Human consumers first: shows the service's saved generated interface (latest
 * version previewed live in the sandboxed iframe, older versions selectable
 * from a version list), or a single-CTA empty state when no interface has been
 * saved yet.
 *
 * Reuses the UI Composer's storage (`GET /generated-endpoint-uis?endpointId=…`
 * via useGeneratedUis) and its hardened PreviewSandbox. There is no standalone
 * viewer route for a saved UI, so the interface lives here and "Copy link"
 * copies this page's Connect-tab URL.
 *
 * That list returns **parent** UIs with their versions nested — `source` and
 * `prompt` exist only on a version — so everything below renders from the
 * flattened version list, never from a parent record (task 396).
 *
 * The preview sizes itself to the whole generated interface (PreviewSandbox
 * measures it inside the frame), so the only control it needs is Hide/Show —
 * open and visible on every visit, never persisted (task 397).
 *
 * Task 1131 moves the UI Composer page's two remaining capabilities here, so
 * nothing is lost when that page retires: Copy source per version, and delete
 * of the interface itself. "Create an interface" sends its request to the
 * Assistant in a new thread (task 1201; see CreateInterfaceButton).
 */
import { useState } from 'react';
import {
  ClipboardDocumentIcon,
  EyeIcon,
  EyeSlashIcon,
  LinkIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import PreviewSandbox from '@/components/ui-composer/PreviewSandbox';
import DeleteResourceControl from '@/components/shared/DeleteResourceControl';
import { useToast } from '@/contexts/ToastContext';
import { useDeleteGeneratedUi } from '@/hooks/useGeneratedUis';
import { findParentUi, versionLabel } from '@/lib/generatedUi';
import type { EndpointUrlParts } from '@/utils/endpointUrl';
import { useServiceInterfaceVersions } from './serviceInterfaceVersions';
import { CreateInterfaceButton } from './CreateInterfaceButton';

interface ServiceInterfaceSectionProps {
  endpointId: string;
  /**
   * The endpoint whose interface this is, when the caller has the row. Only
   * the preview uses it — to inject the service's own invoke URL into the
   * frame (task 938) — so the section still renders from `endpointId` alone.
   */
  endpoint?: EndpointUrlParts | null;
}

export function ServiceInterfaceSection({
  endpointId,
  endpoint,
}: ServiceInterfaceSectionProps) {
  const toast = useToast();
  /** The interface opens visible on every visit; hiding is a per-view choice. */
  const [visible, setVisible] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Which saved version IS the interface — resolved by the shared hook, so this
  // card and the Developer Info tab's preview can never disagree (task 818).
  const { uis, versions, head: latest, current, isLoading } =
    useServiceInterfaceVersions(endpointId);
  const selected =
    (selectedId && versions.find(version => version.id === selectedId)) || current;
  const deleteUi = useDeleteGeneratedUi(endpointId);
  /** The interface the current version belongs to — what Delete removes. */
  const latestParent = latest ? findParentUi(uis, latest) : undefined;

  const handleCopyLink = async () => {
    // Canonical in-app location of this service's interface (no standalone
    // saved-UI viewer route exists — the Connect tab is the interface's home).
    // Canonical tab name since task 800 — `?tab=connect` still resolves via the
    // alias map, but a freshly copied link should carry the current vocabulary.
    const link = `${window.location.origin}/services/${endpointId}?tab=interface`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Interface link copied', link);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  /** Copy one saved version's source — the same copy and toasts the UI Composer used. */
  const handleCopySource = async (source: string, versionNumber: number) => {
    try {
      await navigator.clipboard.writeText(source);
      toast.success(`Source of v${versionNumber} copied to clipboard`);
    } catch {
      toast.error('Copy failed — your browser blocked clipboard access');
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="text-sm text-gray-500 dark:text-gray-400">Loading interface…</div>
      </div>
    );
  }

  if (versions.length === 0 || !selected) {
    return (
      <div
        className="rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-10 text-center"
        data-testid="interface-empty-state"
      >
        <SparklesIcon className="mx-auto h-8 w-8 text-gray-400" />
        <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">
          This service has no interface yet
        </p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Generate a form your team can use — it saves here with version history.
        </p>
        {/* The Assistant entry (task 1131); renders nothing when the Assistant
            is unavailable, since the UI Composer page is gone (task 1132). */}
        <CreateInterfaceButton
          testId="interface-create"
          className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
        />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Card head */}
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Interface</h2>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200">
            Saved · v{latest?.version ?? versions[versions.length - 1]?.version ?? 1}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleCopyLink}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <LinkIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
            Copy link
          </button>
          <button
            type="button"
            onClick={() => setVisible(prev => !prev)}
            className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            {visible ? (
              <EyeSlashIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
            ) : (
              <EyeIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
            )}
            {visible ? 'Hide interface' : 'Show interface'}
          </button>
        </div>
      </div>

      {/* Body: preview + version list. Hiding drops the preview only — the
          version rail stays so versions remain selectable. */}
      <div className="p-4 lg:flex lg:gap-5">
        {visible && (
          <div className="lg:flex-1 min-w-0">
            {/* WHICH version the pane is showing (task 876). The card head says
                what the SERVICE's interface is ("Saved · v4"); this says what is
                in front of you, which is a different thing the moment an older
                version is selected — and the thing that was missing when a pane
                rendered blank under a correct header, leaving nothing to name. */}
            <p
              className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400"
              data-testid="interface-preview-version"
            >
              Showing v{selected.version}
              {selected.id === latest?.id && (
                <span className="ml-1 font-semibold uppercase text-indigo-600 dark:text-indigo-400">
                  · Current
                </span>
              )}
            </p>
            <PreviewSandbox source={selected.source} endpoint={endpoint} />
          </div>
        )}
        <div
          className={
            visible ? 'mt-4 lg:mt-0 lg:w-64 lg:shrink-0' : 'mt-0 lg:w-64 lg:shrink-0'
          }
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
            Versions
          </p>
          <ul className="space-y-1" data-testid="interface-versions">
            {[...versions].reverse().map(version => {
              const versionNumber = version.version;
              const isSelected = version.id === selected.id;
              const isLatest = version.id === latest?.id;
              return (
                <li
                  key={version.id}
                  className={`group flex items-center gap-1 rounded-md ${
                    isSelected
                      ? 'bg-indigo-50 dark:bg-indigo-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setSelectedId(version.id)}
                    className="min-w-0 flex-1 flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs"
                  >
                    <span
                      className={`inline-flex h-5 min-w-[1.75rem] items-center justify-center rounded font-bold ${
                        isLatest
                          ? 'bg-indigo-600 text-white'
                          : 'bg-indigo-50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300'
                      }`}
                    >
                      v{versionNumber}
                    </span>
                    <span className="flex-1 truncate text-gray-700 dark:text-gray-300">
                      {versionLabel(version, findParentUi(uis, version), 'Untitled version')}
                    </span>
                    {isLatest && (
                      <span className="text-[10px] font-semibold uppercase text-indigo-600 dark:text-indigo-400">
                        Current
                      </span>
                    )}
                    <span className="text-gray-400 dark:text-gray-500 shrink-0">
                      {new Date(version.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </button>
                  {/* Copy source (task 1131): the version's saved source, as the
                      UI Composer page's Copy source did. */}
                  <button
                    type="button"
                    onClick={() => void handleCopySource(version.source, versionNumber)}
                    data-testid={`interface-copy-source-v${versionNumber}`}
                    aria-label={`Copy source of v${versionNumber}`}
                    title="Copy source"
                    className="mr-1 shrink-0 rounded p-1 text-gray-400 opacity-0 hover:bg-gray-100 hover:text-gray-600 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                  >
                    <ClipboardDocumentIcon className="h-3.5 w-3.5" />
                  </button>
                </li>
              );
            })}
          </ul>
          <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
            Generated from the Interface tab.
          </p>
          {/* Delete the interface itself (task 1131) — the parent of the current
              version, with every version under it. The same control and copy
              the UI Composer page's saved list used. */}
          {latestParent && (
            <div className="mt-3">
              <DeleteResourceControl
                flag="innovate.fe.services.delete"
                entityLabel="interface"
                name={latest ? versionLabel(latest, latestParent, 'Untitled UI') : 'Untitled UI'}
                id={latestParent.id}
                variant="button"
                triggerLabel="Delete interface"
                testId="interface-delete"
                body={
                  <p>
                    This deletes the saved interface and every version under it, not just this
                    one. If the interface is published, its public page stops answering.
                  </p>
                }
                onDelete={() => deleteUi.mutateAsync(latestParent.id)}
                onDeleted={() => setSelectedId(null)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
